import { ApolloClient, ObservableQuery } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import React, { useMemo, useRef } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useTypedContext from "hooks/useTypedContext";
import { EntityChangePhase, EntityChangeType } from "types/generated";
import { uniqByKey } from "utils/uniq";
import { RunContext } from "components/Run/Context";

import { GET_CHANGES, GetRunChangesData, GetRunChangesVariables } from "./gql";
import { isRunApplied, makeChangesList } from "./helpers";
import styles from "./styles.module.css";
import { RunChangesItem } from "./types";
import VirtualizedLineElement from "./VirtualizedLineElement";

type RunChangesProps = {
  type: EntityChangeType;
};

const CHANGES_LIMIT = 50;

type RunChangesWrapperProps = {
  children: React.ReactNode;
};

const RunChangesWrapper = ({ children }: RunChangesWrapperProps) => {
  return <div className={styles.runChangesWrapper}>{children}</div>;
};

const RunChanges = ({ type: changeType }: RunChangesProps) => {
  const {
    stack: { id: stackId },
    run: { id: runId, history },
  } = useTypedContext(RunContext);
  const { onError } = useTypedContext(FlashContext);

  const {
    data: dataPlanned,
    fetchMore: fetchMorePlanned,
    loading: loadingPlanned,
  } = useQuery(GET_CHANGES, {
    variables: {
      stackId: stackId,
      runId,
      changeType,
      phaseType: EntityChangePhase.Plan,
      limit: CHANGES_LIMIT,
    },
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
  });

  const isApplied = useMemo(() => isRunApplied(history), [history]);

  const {
    data: dataActual,
    fetchMore: fetchMoreActual,
    loading: loadingActual,
  } = usePolledQuery(GET_CHANGES, {
    variables: {
      stackId: stackId,
      runId,
      changeType,
      phaseType: EntityChangePhase.Apply,
      limit: CHANGES_LIMIT,
    },
    shouldPoll: () => !isApplied,
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
  });

  const [planned, actual] = useMemo(
    () => [
      (dataPlanned?.stack?.run?.changesV2?.edges ?? []).map((edge) => edge.node),
      (dataActual?.stack?.run?.changesV2?.edges ?? []).map((edge) => edge.node),
    ],
    [dataPlanned, dataActual]
  );

  const listRef = useRef<VariableSizeList>(null);
  const rowHeights = useRef<Record<string, number>>({});

  const changesList = useMemo<[string, RunChangesItem][]>(
    () => makeChangesList(planned, actual, isApplied),
    [planned, actual, isApplied]
  );

  const setRowHeight = (index: number, size: number) => {
    listRef.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  };

  const getRowHeight = (index: number) => {
    return rowHeights.current[index] + 6 || 28;
  };

  const updateQuery: ObservableQuery.FetchMoreOptions<
    GetRunChangesData,
    GetRunChangesVariables
  >["updateQuery"] = (previous, { fetchMoreResult }) => {
    if (fetchMoreResult?.stack?.run?.changesV2) {
      return {
        stack: {
          ...fetchMoreResult.stack,
          run: {
            ...fetchMoreResult.stack.run,
            changesV2: {
              ...fetchMoreResult.stack.run.changesV2,
              edges: uniqByKey(
                [
                  ...fetchMoreResult.stack.run.changesV2.edges,
                  ...(previous.stack?.run?.changesV2?.edges || []),
                ],
                "cursor"
              ),
            },
          },
        },
      };
    }

    return { stack: previous.stack ? { ...previous.stack } : null };
  };

  const loadMoreItems = async () => {
    const promises: Promise<ApolloClient.QueryResult<GetRunChangesData>>[] = [];

    if (dataPlanned?.stack?.run?.changesV2?.pageInfo?.endCursor) {
      promises.push(
        fetchMorePlanned({
          updateQuery,
          variables: {
            offset: dataPlanned.stack.run.changesV2.pageInfo.endCursor,
          },
        })
      );
    }

    if (dataActual?.stack?.run?.changesV2?.pageInfo?.endCursor) {
      promises.push(
        fetchMoreActual({
          updateQuery,
          variables: {
            offset: dataActual.stack.run.changesV2.pageInfo.endCursor,
          },
        })
      );
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      onError(error);
    }
  };

  const isItemLoaded = (value: number) => {
    return value < changesList.length;
  };

  if ((loadingPlanned && !dataPlanned?.stack?.run) || (loadingActual && !dataActual?.stack?.run)) {
    return <PageLoading />;
  }

  return (
    <RunChangesWrapper>
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        itemCount={changesList.length + CHANGES_LIMIT}
        loadMoreItems={loadMoreItems}
      >
        {({ onItemsRendered }) => (
          <AutoSizer disableWidth>
            {({ height }) => (
              <VariableSizeList
                itemKey={(index) => changesList[index][0]}
                itemData={{
                  changesList,
                  setRowHeight,
                }}
                itemCount={changesList.length}
                itemSize={getRowHeight}
                ref={listRef}
                width="100%"
                height={height}
                onItemsRendered={onItemsRendered}
              >
                {VirtualizedLineElement}
              </VariableSizeList>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </RunChangesWrapper>
  );
};

export default RunChanges;
